import React, { useCallback, useRef } from 'react';
import { trackJoinBackstage, isBackstageLink, trackCTAHover } from '../utils/metaPixel';
interface ButtonProps {
  children: React.ReactNode;
  href?: string;
  onClick?: () => void;
  variant?: 'primary' | 'secondary';
  className?: string;
  trackingLabel?: string;
}
export function Button({
  children,
  href,
  onClick,
  variant = 'primary',
  className = ''
}: ButtonProps) {
  const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasTrackedHoverRef = useRef(false);
  const baseStyles = 'group relative inline-flex items-center justify-center px-10 py-5 text-sm font-medium tracking-widest uppercase transition-all duration-500 rounded-sm overflow-hidden';
  const variantStyles = {
    primary: 'bg-gradient-to-r from-[#D4AF37] via-[#C5A028] to-[#D4AF37] bg-size-200 bg-pos-0 hover:bg-pos-100 text-white shadow-lg shadow-[#D4AF37]/30 hover:shadow-2xl hover:shadow-[#D4AF37]/50 hover:scale-105 hover:-translate-y-1',
    secondary: 'border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white hover:shadow-lg hover:shadow-[#D4AF37]/30 hover:scale-105'
  };
  const combinedClassName = `${baseStyles} ${variantStyles[variant]} ${className}`;
  // Handle hover intent tracking (hover > 1.5s)
  const handleMouseEnter = useCallback(() => {
    if (href && isBackstageLink(href) && !hasTrackedHoverRef.current) {
      hoverTimerRef.current = setTimeout(() => {
        trackCTAHover();
        hasTrackedHoverRef.current = true;
      }, 1500); // 1.5 seconds
    }
  }, [href]);
  const handleMouseLeave = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);
  // Handle click with Meta Pixel tracking
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    // Clear hover timer if still running
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    // Check if this is a Backstage link
    if (href && isBackstageLink(href)) {
      // Fire Meta Pixel event with enhanced parameters
      trackJoinBackstage();
    }
    // Call original onClick if provided
    if (onClick) {
      onClick();
    }
    // Allow normal navigation to proceed
    // Links open in new tab (target="_blank"), so event has time to fire
  };
  const content = <>
      <span className="relative z-10 flex items-center gap-2 transition-transform duration-300 group-hover:gap-3" data-id="element-45">
        {children}
      </span>
      {variant === 'primary' && <>
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" data-id="element-46" />
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-47">
            <div className="absolute top-0 left-1/4 w-2 h-2 bg-white/40 rounded-full animate-sparkle" data-id="element-48" />
            <div className="absolute top-1/2 right-1/4 w-1 h-1 bg-white/40 rounded-full animate-sparkle-delay" data-id="element-49" />
          </div>
        </>}
    </>;
  if (href) {
    return <a href={href} target="_blank" rel="noopener noreferrer" className={combinedClassName} onClick={handleClick} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} data-id="element-50">
        {content}
      </a>;
  }
  return <button onClick={handleClick} className={combinedClassName} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} data-id="element-51">
      {content}
    </button>;
}